import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { patientAPI } from '../services/api';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';

const PatientForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});  // ADD THIS LINE
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  useEffect(() => {
    if (id) {
      fetchPatient();
    }
  }, [id]);

const fetchPatient = async () => {
  try {
    setLoading(true);
    const response = await patientAPI.getById(id);
    const patientData = response.data;
    
    // ADD THIS BLOCK - format date for input
    if (patientData.dob) {
      patientData.dob = format(new Date(patientData.dob), 'yyyy-MM-dd');
    }
    
    reset(patientData);
  } catch (err) {
    setError('Failed to load patient data');
    console.error('Error fetching patient:', err);
  } finally {
    setLoading(false);
  }
};

const onSubmit = async (data) => {
  try {
    setLoading(true);
    setError('');
    setFieldErrors({});  // ADD THIS LINE
    
    // ADD THIS BLOCK - ensure date is in correct format
    const submitData = { ...data };
    if (submitData.dob) {
      submitData.dob = format(new Date(submitData.dob), 'yyyy-MM-dd');
    }
    
    if (id) {
      await patientAPI.update(id, submitData);
    } else {
      await patientAPI.create(submitData);
    }
    navigate('/dashboard/patients');
  } catch (err) {
    console.error('Error saving patient:', err);
    
    // ADD THIS BLOCK - handle validation errors
    if (err.response?.data?.errors) {
      const validationErrors = {};
      err.response.data.errors.forEach(error => {
        validationErrors[error.path] = error.msg;
      });
      setFieldErrors(validationErrors);
      setError('Please fix the validation errors below');
    } else {
      setError(`Failed to ${id ? 'update' : 'create'} patient`);
    }
  } finally {
    setLoading(false);
  }
};

  if (loading && id) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 text-lg">Loading patient data...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-800">
          {id ? 'Edit Patient' : 'Add New Patient'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {id ? 'Update patient information below' : 'Enter new patient details below'}
        </p>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      
      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
  {/* Name Field */}
  <div>
    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
      Full Name <span className="text-red-500">*</span>
    </label>
    <input
      id="name"
      type="text"
      {...register('name', { required: 'Name is required' })}
      disabled={loading}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 ${
        fieldErrors.name ? 'border-red-500' : 'border-gray-300'
      }`}
      placeholder="John Doe"
    />
    {errors.name && (
      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
    )}
    {fieldErrors.name && (
      <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
    )}
  </div>

  {/* Email Field */}
  <div>
    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
      Email Address
    </label>
    <input
      id="email"
      type="email"
      {...register('email')}
      disabled={loading}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 ${
        fieldErrors.email ? 'border-red-500' : 'border-gray-300'
      }`}
      placeholder="john@example.com"
    />
    {fieldErrors.email && (
      <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
    )}
  </div>

  {/* Phone Field */}
  <div>
    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
      Phone Number
    </label>
    <input
      id="phone"
      type="tel"
      {...register('phone')}
      disabled={loading}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 ${
        fieldErrors.phone ? 'border-red-500' : 'border-gray-300'
      }`}
      placeholder="(555) 123-4567"
    />
    {fieldErrors.phone && (
      <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>
    )}
  </div>

  {/* Date of Birth Field */}
  <div>
    <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1">
      Date of Birth
    </label>
    <input
      id="dob"
      type="date"
      {...register('dob')}
      disabled={loading}
      max={format(new Date(), 'yyyy-MM-dd')}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 ${
        fieldErrors.dob ? 'border-red-500' : 'border-gray-300'
      }`}
    />
    {fieldErrors.dob && (
      <p className="mt-1 text-sm text-red-600">{fieldErrors.dob}</p>
    )}
    <p className="mt-1 text-xs text-gray-500">Format: YYYY-MM-DD</p>
  </div>

  {/* Medical Notes Field */}
  <div>
    <label htmlFor="medical_notes" className="block text-sm font-medium text-gray-700 mb-1">
      Medical Notes
    </label>
    <textarea
      id="medical_notes"
      rows="4"
      {...register('medical_notes')}
      disabled={loading}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 resize-y ${
        fieldErrors.medical_notes ? 'border-red-500' : 'border-gray-300'
      }`}
      placeholder="Enter any relevant medical notes, allergies, or conditions..."
    />
    {fieldErrors.medical_notes && (
      <p className="mt-1 text-sm text-red-600">{fieldErrors.medical_notes}</p>
    )}
  </div>

  {/* Action Buttons */}
  <div className="flex gap-3 pt-4 border-t border-gray-200">
    <button
      type="submit"
      disabled={loading}
      className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {loading ? (
        <span className="flex items-center">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {id ? 'Updating...' : 'Saving...'}
        </span>
      ) : (
        id ? 'Update Patient' : 'Create Patient'
      )}
    </button>
    
    <button
      type="button"
      onClick={() => navigate('/dashboard/patients')}
      disabled={loading}
      className="px-4 py-2 bg-white text-gray-700 font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      Cancel
    </button>
  </div>
</form>
    </div>
  );
};

export default PatientForm;